import { useState, useEffect } from "react";
import { Icon } from '@iconify/react/dist/iconify.js';
import axios from 'axios'; 
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; 

import ComplaintUploadWithImagePreviewList from "./child/ComplaintUploadWithImagePreviewList";

const StandAloneComplainFormPage = () => {
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
  const [uploadedEvidenceUrls, setUploadedEvidenceUrls] = useState([]);
  const [anonymize, setAnonymize] = useState(false);
  const [formData, setFormData] = useState({
    dept_id: "",
    district_id: "",
    complaint_nature_id: "",
    complaint_nearby_place: "",
    complaint_detail: "",
    complainant_name: "",
    complainant_address: "",
    latitude: "",
    longitude: "",
    complainant_phone: "",
    complainant_email: "",
    complaint_time: "",
    complaint_additional_remarks: "",
    complainant_phone_secondry: "",
    other_nature: "",
    received_by: "",
    status: "Open",
    priority_level: "",
    cnic: "",
    complaint_source: "",
    complainant_city: "",
    complainant_tehsil: "",
    complainant_district: "",
    complaint_city: "",
    complaint_tehsil: "",
    complaint_district: "",
    complaint_address: ""
  });

  const [departments, setDepartments] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [tehsils, setTehsils] = useState([]); // For Complainant Tehsil (all tehsils)
  const [complaintTehsils, setComplaintTehsils] = useState([]); // For Complaint Tehsil (from geolocation API)
  const [complaintNatures, setComplaintNatures] = useState([]);
  const [showOtherInput, setShowOtherInput] = useState(false);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setFormData((prev) => ({
            ...prev,
            latitude: latitude.toString(),
            longitude: longitude.toString()
          }));
          
          // Fetch district and tehsils based on latitude and longitude
          const fetchDistrictAndTehsil = async () => {
            try {
              const response = await axios.post(`${API_BASE_URL}/backend/complaints/get-district-and-tehsil-from-lat-long`, {
                lat: latitude,
                long: longitude
              });
              if (response.data.status) {
                setFormData((prev) => ({
                  ...prev,
                  complaint_district: response.data.district._id
                }));
                setComplaintTehsils(response.data.tehsils); // Set tehsils for Complaint Tehsil dropdown
              }
            } catch (error) {
              toast.error("Failed to fetch district and tehsils from coordinates.");
              console.error("Error fetching district and tehsils:", error);
            }
          };
          fetchDistrictAndTehsil();
        },
        (error) => {
          console.error("Unable to retrieve your location. Please enable location services.", error);
          toast.error("Unable to retrieve location. Please enable location services.");
        }
      );
    } else {
      console.error("Geolocation is not supported by your browser.");
      toast.error("Geolocation is not supported by your browser.");
    }
  }, [API_BASE_URL]);

  // Fetch Departments
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/backend/departments/get-all-departments`);
        setDepartments(response.data.data);
      } catch (error) {
        toast.error("Failed to fetch departments.");
        console.error("Error fetching departments:", error);
      }
    };
    fetchDepartments();
  }, [API_BASE_URL]);

  // Fetch Districts
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/backend/districts/get-all-districts`);
        setDistricts(response.data.data);
      } catch (error) {
        toast.error("Failed to fetch districts.");
        console.error("Error fetching districts:", error);
      }
    };
    fetchDistricts();
  }, [API_BASE_URL]);

  // Fetch Cities
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/backend/generic/city`);
        setCities(response.data.data);
      } catch (error) {
        toast.error("Failed to fetch cities.");
        console.error("Error fetching cities:", error);
      }
    };
    fetchCities();
  }, [API_BASE_URL]);

  // Fetch Tehsils (All Tehsils for Complainant Tehsil)
  useEffect(() => {
    const fetchTehsils = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/backend/generic/tehsil`);
        setTehsils(response.data.data);
      } catch (error) {
        toast.error("Failed to fetch tehsils.");
        console.error("Error fetching tehsils:", error);
      }
    };
    fetchTehsils();
  }, [API_BASE_URL]);

  // Fetch Complaint Natures
  useEffect(() => {
    const fetchComplaintNatures = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/backend/complaint-natures/get-all-complaint-natures`);
        setComplaintNatures(response.data.data);
      } catch (error) {
        toast.error("Failed to fetch complaint natures.");
        console.error("Error fetching complaint natures:", error);
      }
    };
    fetchComplaintNatures();
  }, [API_BASE_URL]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "cnic") {
      let formattedValue = value.replace(/\D/g, ''); 
      if (formattedValue.length > 5) {
        formattedValue = formattedValue.substring(0, 5) + '-' + formattedValue.substring(5);
      }
      if (formattedValue.length > 13) {
        formattedValue = formattedValue.substring(0, 13) + '-' + formattedValue.substring(13);
      }
      formattedValue = formattedValue.substring(0, 15);
      setFormData({
        ...formData,
        [name]: formattedValue,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const cnicRegex = /^[0-9]{5}-[0-9]{7}-[0-9]{1}$/;

    if (name === "cnic" && value && !cnicRegex.test(value)) {
      toast.error("Please enter a valid CNIC in the format 12345-6789012-3");
    }
    const pakPhoneRegex = /^(\+92|0)3[0-9]{9}$/;

    if ((name === "complainant_phone" || name === "complainant_phone_secondry") && value && !pakPhoneRegex.test(value)) {
      toast.error("Please enter a valid Pakistani phone number (e.g., +923001234567 or 03001234567)");
    }
  };

  const handleSelectChange = (e) => {
    const { value, name } = e.target;
    setFormData({ ...formData, [name]: value });

    // Show other nature input field when "Other" is selected
    if (name === "complaint_nature_id") {
      const selectedNature = complaintNatures.find(nature => nature._id === value);
      if (selectedNature && selectedNature.name.toLowerCase() === "other") {
        setShowOtherInput(true);
      } else {
        setShowOtherInput(false);
        setFormData(prev => ({ ...prev, other_nature: "" }));
      }
    }

    // Validate Complaint Tehsil on change
    if (name === "complaint_tehsil" && value) {
      const isValidTehsil = complaintTehsils.some(tehsil => tehsil._id === value);
      if (!isValidTehsil) {
        toast.error("Please select a valid tehsil for the complaint location.");
        setFormData(prev => ({ ...prev, complaint_tehsil: "" }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const currentDateTime = new Date().toISOString();
    setFormData((prevData) => ({
      ...prevData,
      complaint_time: currentDateTime,
    }));

    const requiredFields = [
      "complainant_name",
      "complainant_address",
      "complainant_phone",
      "complaint_detail",
      "complainant_district",
      "dept_id",
      "complaint_nature_id",
      "status",
      "complaint_nearby_place",
      "complaint_additional_remarks",
      "cnic",
      "complainant_city",
      "complainant_tehsil",
      "complaint_city",
      "complaint_tehsil",
      "complaint_district",
      "complaint_address",
      "latitude",
      "longitude"
    ];

    const isFormValid = requiredFields.every((field) => formData[field] !== "");

    if (!isFormValid) {
      toast.error("Please fill in all required details!");
      return;
    }

    const pakPhoneRegex = /^(\+92|0)3[0-9]{9}$/;
    if (!pakPhoneRegex.test(formData.complainant_phone)) {
      toast.error("Please enter a valid Primary phone number (e.g., +923001234567 or 03001234567)");
      return;
    }

    if (formData.complainant_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.complainant_email)) {
      toast.error("Invalid email format!");
      return;
    }

    // Validate Complaint Tehsil
    if (formData.complaint_tehsil) {
      const isValidTehsil = complaintTehsils.some(tehsil => tehsil._id === formData.complaint_tehsil);
      if (!isValidTehsil) {
        toast.error("Please select a valid tehsil for the complaint location.");
        return;
      }
    } else {
      toast.error("Complaint Tehsil is required.");
      return;
    }

    // Prepare data for API
    const apiData = {
      complainant_name: formData.complainant_name,
      complainant_address: formData.complainant_address,
      complainant_email: formData.complainant_email,
      complainant_contact_no: formData.complainant_phone,
      priority_level: "Critical",
      complaint_detail: formData.complaint_detail,
      complainant_district_id: formData.complainant_district,
      dept_id: formData.dept_id,
      complaint_nature_id: formData.complaint_nature_id,
      near_by_place: formData.complaint_nearby_place,
      remarks: formData.complaint_additional_remarks,
      status: "Open",
      complainant_cnic: formData.cnic,
      complaint_source: "Public Form",
      anonymize: anonymize,
      ...(showOtherInput && { other_nature: formData.other_nature }),
      complainant_city_id: formData.complainant_city,
      complainant_tehsil_id: formData.complainant_tehsil,
      complaint_city_id: formData.complaint_city,
      complaint_tehsil_id: formData.complaint_tehsil,
      complaint_district_id: formData.complaint_district,
      complaint_address: formData.complaint_address,
      media_array: uploadedEvidenceUrls,
      lat: formData.latitude,
      long: formData.longitude
    };

    try {
      const response = await axios.post(`${API_BASE_URL}/backend/complaints/create-public-complaint`, apiData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status) {
        toast.success("Complaint Submitted Successfully!");
        setUploadedEvidenceUrls([]);
        setFormData({
          dept_id: "",
          district_id: "",
          complaint_nature_id: "",
          complaint_nearby_place: "",
          complaint_detail: "",
          complainant_name: "",
          complainant_address: "",
          complainant_phone: "",
          complainant_email: "",
          complaint_time: "",
          complaint_additional_remarks: "",
          complainant_phone_secondry: "",
          received_by: "",
          status: "Open",
          priority_level: "",
          cnic: "",
          complaint_source: "",
          other_nature: "",
          complainant_city: "",
          complainant_tehsil: "",
          complainant_district: "",
          complaint_city: "",
          complaint_tehsil: "",
          complaint_district: "",
          complaint_address: ""
        });
        setAnonymize(false);
        setComplaintTehsils([]); // Reset complaint tehsils after submission
      }
    } catch (error) {
      if (error.response) {
        console.error("Backend said:", error.response.data);
        toast.error(error.response.data.message || "Failed to submit complaint.");
      } else {
        console.error("Other Error:", error);
        toast.error("Something went wrong.");
      }
    }
  };

  return (
    <div className="complaint-form col-lg-12 col-md-12 col-sm-12">
      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="">
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-green-900 to-emerald-800 p-8 shadow-2xl shadow-emerald-900/40">
              <div className="absolute -bottom-20 -left-20 h-40 w-40 rounded-full bg-blue-400/10 blur-2xl"></div>
              <div className="relative z-10 text-center">
                <div className="mb-2">
                  <span className="text-sm font-semibold uppercase tracking-widest text-success-300">
                    Department of Forestry & Wildlife
                  </span>
                </div>
                <h1 className="text-5xl font-bold leading-tight text-white">
                  <span className="text-3xl font-medium text-emerald-100 text-success-500">
                    Public Complaint Form
                  </span>
                </h1>
                <p className="text-lg italic text-emerald-100/90 text-success-300">
                  "Protecting our forests, wildlife and aquatic ecosystems"
                </p>
              </div>
            </div>

            <div className="text-xl text-white font-bold">Complainant Information</div>
            <hr className="pb-05"/>
            <div className="row gy-3">
              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:office-building" className="text-md" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}/>
                  Department
                </label>
                <select
                  className="form-select"
                  name="dept_id"
                  value={formData.dept_id}
                  onChange={handleSelectChange}
                  required
                  style={{ backgroundColor: 'var(--white)' }}
                >
                  <option value="">Select Department</option>
                  {departments.map((dept) => (
                    <option key={dept._id} value={dept._id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-white align-items-center gap-2">
                  <Icon icon="mdi:account" className="text-lg" />
                  <span className="text-md" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>Complainant Name</span>
                </label>
                <input
                  type="text"
                  name="complainant_name"
                  placeholder="Complainant Name"
                  className="form-control"
                  value={formData.complainant_name}
                  onChange={handleInputChange}
                  required
                  style={{ backgroundColor: 'var(--white)' }}
                />
              </div>

              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:phone" className="text-lg" />
                  Complainant Phone
                </label>
                <div className="d-flex gap-2 align-items-start">
                  <input
                    type="text"
                    name="complainant_phone"
                    placeholder="Phone Number (e.g., +923001234567 or 03001234567)"
                    className="form-control"
                    value={formData.complainant_phone}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    pattern="(\+92|0)3[0-9]{9}"
                    maxLength="13"
                    title="Please enter a valid Pakistani phone number"
                    required
                    style={{ backgroundColor: 'var(--white)' }}
                  />
                </div>
              </div>

              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:card-account-details" className="text-lg" />
                  Complainant CNIC
                </label>
                <input
                  type="text"
                  name="cnic"
                  placeholder="CNIC (e.g., 12345-6789012-3)"
                  className="form-control"
                  value={formData.cnic}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  pattern="[0-9]{5}-[0-9]{7}-[0-9]{1}"
                  maxLength="15"
                  title="Please enter a valid CNIC in the format 12345-6789012-3"
                  required
                  style={{ backgroundColor: 'var(--white)' }}
                />
              </div>

              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:email" className="text-lg" />
                  Complainant Email
                </label>
                <input
                  type="text"
                  name="complainant_email"
                  placeholder="info@gmail.com"
                  className="form-control"
                  value={formData.complainant_email}
                  onBlur={handleBlur}
                  onChange={handleInputChange}
                  style={{ backgroundColor: 'var(--white)' }}
                />
              </div>

              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:home" className="text-lg" />
                  Complainant Address
                </label>
                <input
                  type="text"
                  name="complainant_address"
                  placeholder="Complainant Address"
                  className="form-control"
                  value={formData.complainant_address}
                  onChange={handleInputChange}
                  required
                  style={{ backgroundColor: 'var(--white)' }}
                />
              </div>

              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:map-marker-radius" className="text-lg" />
                  Nearby Landmark of Complainant
                </label>
                <input
                  type="text"
                  name="complaint_nearby_place"
                  placeholder="Nearby Landmark of Complainant"
                  className="form-control"
                  value={formData.complaint_nearby_place}
                  onChange={handleInputChange}
                  required
                  style={{ backgroundColor: 'var(--white)' }}
                />
              </div>

              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:city" className="text-lg" />
                  Complainant City 
                </label>
                <select
                  className="form-select"
                  name="complainant_city"
                  value={formData.complainant_city}
                  onChange={handleSelectChange}
                  style={{ backgroundColor: 'var(--white)' }}
                >
                  <option value="">Select City</option>
                  {cities.map((city) => (
                    <option key={city._id} value={city._id}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:map-marker" className="text-lg" />
                  Complainant Tehsil
                </label>
                <select
                  className="form-select"
                  name="complainant_tehsil"
                  value={formData.complainant_tehsil}
                  onChange={handleSelectChange}
                  required
                  style={{ backgroundColor: 'var(--white)' }}
                >
                  <option value="">Select Tehsil</option>
                  {tehsils.map((tehsil) => (
                    <option key={tehsil._id} value={tehsil._id}>
                      {tehsil.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:map-marker" className="text-lg" />
                  Complainant District
                </label>
                <select
                  className="form-select"
                  name="complainant_district"
                  value={formData.complainant_district}
                  onChange={handleSelectChange}
                  required
                  style={{ backgroundColor: 'var(--white)' }}
                >
                  <option value="">Select District</option>
                  {districts.map((district) => (
                    <option key={district._id} value={district._id}>
                      {district.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:map-marker" className="text-lg" />
                  Latitude
                </label>
                <input
                  type="text"
                  name="latitude"
                  placeholder="Latitude"
                  className="form-control"
                  value={formData.latitude}
                  onChange={handleInputChange}
                  readOnly
                />
              </div>
              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:map-marker" className="text-lg" />
                  Longitude
                </label>
                <input
                  type="text"
                  name="longitude"
                  placeholder="Longitude"
                  className="form-control"
                  value={formData.longitude}
                  onChange={handleInputChange}
                  readOnly
                />
              </div>
            </div>
            <div className="row my-3 mx-1">
              <div className="text-xl text-white font-bold">Complaint Information</div>
              <hr/>
            </div>
            
            <div className="row gy-3">
              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:alert-circle-outline" className="text-lg" />
                  Nature of Complaint
                </label>
                <select
                  className="form-select"
                  name="complaint_nature_id"
                  value={formData.complaint_nature_id}
                  onChange={handleSelectChange}
                  required
                  style={{ backgroundColor: 'var(--white)' }}
                >
                  <option value="">Select a complaint type</option>
                  {complaintNatures.map((nature) => (
                    <option key={nature._id} value={nature._id}>
                      {nature.name}
                    </option>
                  ))}
                </select>
                
                {showOtherInput && (
                  <div className="mt-3">
                    <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                      <Icon icon="mdi:pencil-outline" className="text-lg" />
                      Specify Other Nature
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="other_nature"
                      value={formData.other_nature}
                      onChange={handleInputChange}
                      placeholder="Please specify the nature of the complaint"
                      style={{ backgroundColor: 'var(--white)' }}
                    />
                  </div>
                )}
              </div>
              
              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:home" className="text-lg" />
                  Complaint Address
                </label>
                <input
                  type="text"
                  name="complaint_address"
                  placeholder="Complaint Address"
                  className="form-control"
                  value={formData.complaint_address}
                  onChange={handleInputChange}
                  style={{ backgroundColor: 'var(--white)' }}
                />
              </div>
              
              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:map-marker" className="text-lg" />
                  Complaint District
                </label>
                <select
                  className="form-select"
                  name="complaint_district"
                  value={formData.complaint_district}
                  onChange={handleSelectChange}
                  style={{ backgroundColor: 'var(--white)' }}
                >
                  <option value="">Select District</option>
                  {districts.map((district) => (
                    <option key={district._id} value={district._id}>
                      {district.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:map-marker" className="text-lg" />
                  Complaint Tehsil
                </label>
                <select
                  className="form-select"
                  name="complaint_tehsil"
                  value={formData.complaint_tehsil}
                  onChange={handleSelectChange}
                  required
                  style={{ backgroundColor: 'var(--white)' }}
                >
                  <option value="">Select Tehsil</option>
                  {complaintTehsils.map((tehsil) => (
                    <option key={tehsil._id} value={tehsil._id}>
                      {tehsil.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:city" className="text-lg" />
                  Complaint City 
                </label>
                <select
                  className="form-select"
                  name="complaint_city"
                  value={formData.complaint_city}
                  onChange={handleSelectChange}
                  style={{ backgroundColor: 'var(--white)' }}
                >
                  <option value="">Select City</option>
                  {cities.map((city) => (
                    <option key={city._id} value={city._id}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="col-lg-4 col-md-6 col-sm-12"></div>
              
              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:file-document" className="text-lg" />
                  Complaint Detail
                </label>
                <textarea
                  name="complaint_detail"
                  className="form-control"
                  rows={4}
                  placeholder="Enter a description..."
                  value={formData.complaint_detail}
                  onChange={handleInputChange}
                  required
                  style={{ backgroundColor: 'var(--white)' }}
                />
              </div>

              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:note-text" className="text-lg" />
                  Additional Remarks
                </label>
                <textarea
                  name="complaint_additional_remarks"
                  className="form-control"
                  rows={4}
                  placeholder="Additional Remarks..."
                  value={formData.complaint_additional_remarks}
                  onChange={handleInputChange}
                  style={{ backgroundColor: 'var(--white)' }}
                />
              </div>
              <div className="col-lg-4 col-md-6 col-sm-12">
                <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                  <Icon icon="mdi:file-upload" className="text-lg" />
                  Evidence if any
                </label>
                <ComplaintUploadWithImagePreviewList
                  onFileUploadSuccess={(url) => {
                    setUploadedEvidenceUrls(prev => [...prev, url]);
                  }}
                />
                <label className="text-sm mt-1 mb-0">Upload up to 5 supported files of Pdf, word or Jpg format (Max 5 MB)</label>
              </div>
            </div>

            <div className="col-lg-12 col-md-12 col-sm-12 my-3">
              <label className="form-label d-flex text-md text-white align-items-center gap-2" style={{ textShadow: '0 0 15px rgba(0, 0, 0, 1)'}}>
                <input
                  type="checkbox"
                  checked={anonymize}
                  onChange={(e) => setAnonymize(e.target.checked)}
                  className="form-check-input"
                />
                Please click here to keep your personal information confidential during the process.
              </label>
            </div>
            
            <div className="d-flex justify-content-end">
              <button
                className="btn btn-success text-sm btn-sm px-6 py-12 radius-12 my-4 mx-10 col-lg-2 col-md-4 col-sm-12"
                type="submit"
              >
                Submit Complaint
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StandAloneComplainFormPage;
